<template>
  <ui-dialog
    :show="isOpen"
    max-width="623"
    @input="handleClose"
    custom-class="bulk-reply-dialog"
  >
    <template #header>
      <div class="bulk-reply-dialog__header">
        <h3>
          {{ $t("message.reviews.bulkReply.heading") }}
        </h3>
      </div>

      <v-radio-group
        v-if="canUseReviewTemplates"
        v-model="replyType"
        color="primary"
        row
        class="px-1"
      >
        <v-radio
          :label="$t('message.reviews.bulkReply.selectCustomReply')"
          :value="replyTypes.CUSTOM"
        ></v-radio>
        <v-radio
          :label="$t('message.reviews.bulkReply.selectTemplateReply')"
          :value="replyTypes.TEMPLATE"
        ></v-radio>
      </v-radio-group>

      <InfoBox
        :text="
          $t(
            `message.reviews.bulkReply.${
              isTemplateMode ? 'templateInfoBoxLabel' : 'infoBoxLabel'
            }`
          )
        "
      />

      <InfoBox
        class="mt-2"
        :text="$t(`message.reviews.bulkReply.infoBoxLabel2`)"
      />

      <v-select
        v-if="isTemplateMode"
        class="mt-5"
        :label="$t('message.reviews.bulkReply.category')"
        :items="categories"
        v-model="activeCategory"
        item-text="name"
        item-value="id"
        hide-details
      />
    </template>

    <div class="py-1 mt-4" v-if="!isTemplateMode">
      <v-textarea
        v-model="replyText"
        ref="input"
        :label="$t('message.reviews.bulkReply.bulkReplyPlaceholder')"
        clearable
        autofocus
        :hide-details="true"
        rows="4"
        class="bulk-reply-dialog__input"
      ></v-textarea>
    </div>

    <BulkReplyTemplates
      v-else
      v-model="selectedTemplateIds"
      :category-id="activeCategory"
      :templates="replyTemplates"
      :loading="templatesLoading"
    />

    <template #actions>
      <div class="w-100 d-flex justify-center">
        <ui-button
          @click="handleClose"
          color="primary"
          outlined
          min-width="130"
          class="mr-2"
        >
          {{ $t("message.common.cancel") }}
        </ui-button>
        <ui-button
          @click="handleSave"
          :disabled="isSubmitDisabled"
          color="primary"
          min-width="130"
          class="ml-2"
        >
          {{ $t("message.reviews.bulkReply.saveBtnLabel") }}
        </ui-button>
      </div>
    </template>
  </ui-dialog>
</template>

<script>
import InfoBox from "@/components/ui/InfoBox.vue";
import BulkReplyTemplates from "@/components/Reviews/BulkReply/BulkReplyTemplates";
import serviceHandler from "@/helpers/handlers/service";
import reviewsService from "@/services/reviews";
import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";
import { planRestrictionsCheck } from "@/helpers/plan-restrictions-check";

export default {
  name: "BulkAnswerDialog",
  components: {
    InfoBox,
    BulkReplyTemplates
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      replyText: "",
      replyType: 1,
      replyTypes: {
        CUSTOM: 1,
        TEMPLATE: 2
      },
      templatesLoading: false,
      activeCategory: 0,
      replyCategories: [],
      replyTemplates: [],
      selectedTemplateIds: []
    };
  },

  computed: {
    canUseReviewTemplates() {
      return planRestrictionsCheck(PLAN_RESTRICTIONS.CAN_USE_REVIEW_TEMPLATES);
    },
    isTemplateMode() {
      return this.replyType === this.replyTypes.TEMPLATE;
    },
    categories() {
      return [
        { name: this.$t("message.reviews.bulkReply.all"), id: 0 },
        ...this.replyCategories
      ];
    },
    isSubmitDisabled() {
      return this.isTemplateMode
        ? !this.selectedTemplateIds.length
        : !this.replyText;
    }
  },

  watch: {
    async isOpen(newValue) {
      if (newValue) {
        this.selectedTemplateIds = [];
        this.replyType = this.replyTypes.CUSTOM;
        this.initTemplatesData();
      }
    }
  },

  methods: {
    handleSave() {
      if (this.isSubmitDisabled) return;

      if (this.isTemplateMode) {
        this.$emit("reply-templates", this.selectedTemplateIds);
      } else {
        this.$emit("reply", this.replyText);
      }

      this.selectedTemplateIds = [];
      this.replyText = "";

      this.handleClose();
    },
    handleClose() {
      this.$emit("close");
    },
    async initTemplatesData() {
      this.templatesLoading = true;
      await Promise.all([
        this.fetchReplyCategories(),
        this.fetchReplyTemplates()
      ]);
      this.templatesLoading = false;
    },
    async fetchReplyCategories() {
      this.replyCategories = await serviceHandler(
        reviewsService.getReplyCategories()
      );
    },
    async fetchReplyTemplates() {
      this.replyTemplates = (
        await serviceHandler(
          reviewsService.getReplyTemplates({ categoryIds: [] })
        )
      ).filter(i => !!i.text);
    },
    onSelectedTemplatesChange(ids) {
      this.selectedTemplateIds = [...ids];
    }
  }
};
</script>

<style lang="scss">
.v-dialog.bulk-reply-dialog {
  .v-card__title {
    padding-bottom: 1px !important;
  }

  .dialog-header {
    margin-right: 0 !important;
  }

  .dialog-content .v-card__text {
    padding: 0 $spacing * 4 !important;
  }

  .v-input--selection-controls {
    padding-top: 0;
  }

  .items-counter,
  .v-input--selection-controls label {
    font-weight: 500;
    @include font-size(14px);
    line-height: 17px;
    color: $dark-blue-3;
  }
}
</style>

<style lang="scss" scoped>
.bulk-reply-dialog {
  &__header {
    flex: 1 1 auto;
    text-align: center;
  }

  &__body {
    padding: $spacing * 0.5;
  }

  &__input {
    width: 100%;
  }
}
</style>
